import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Upload, Download, FileText, FileSpreadsheet, Check, AlertCircle, 
  FolderUp, Loader2, X, CheckCircle, AlertTriangle, Info, Eye, ArrowRight
} from 'lucide-react';
import { apiErrorMessage } from '../lib/apiError';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, 
  DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { Action, can as evalCan } from '../lib/auth';
import { toast } from 'sonner';
import api from '../lib/api';

const ImportExportPage = () => {
  // IMPORT_EXPORT is plan-level project work, and this page acts on OTHER
  // projects (the import target / export source), not just the active one.
  // So evaluate the permission against the TARGET project's role rather than a
  // single active-project gate — otherwise a user who is plan on the active
  // project but only view on the target would see enabled import/export.
  const { can } = useProject();
  const { user } = useAuth();
  // Per-target check: the chosen project must carry IMPORT_EXPORT for this user.
  // `projects` (from /projects) includes the caller's `my_role` on each project.
  const canImportExportFor = (projectId) => {
    if (!projectId) return false;
    const proj = projects.find((p) => p.id === projectId);
    return evalCan(Action.IMPORT_EXPORT, { orgRole: user?.role, projectRole: proj?.my_role });
  };
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // State
  const [projects, setProjects] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [importHistory, setImportHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Import form state
  const [selectedFile, setSelectedFile] = useState(null);
  const [importMode, setImportMode] = useState('new_project');
  const [existingProjectId, setExistingProjectId] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [newScheduleName, setNewScheduleName] = useState('');
  
  // Validation preview state
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  
  // Result dialog
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Export state
  const [selectedExportProject, setSelectedExportProject] = useState('');
  const [selectedExportSchedule, setSelectedExportSchedule] = useState('');

  // Whether the user may run the import for the CURRENT mode/target:
  //  - new_schedule writes into an existing project → need IMPORT_EXPORT on it.
  //  - new_project creates a fresh project → planner-grant CREATE_PROJECT (planner /
  //    super_admin only), mirroring the backend's create-project gate. The
  //    importer becomes the initial PLAN member on the new project.
  const canCreateProject = evalCan(Action.CREATE_PROJECT, { orgRole: user?.role, isPlanner: !!user?.is_planner });
  const canPerformImport =
    importMode === 'new_schedule'
      ? canImportExportFor(existingProjectId)
      : canCreateProject;

  // Supported formats
  const importFormats = [
    { name: 'Primavera P6 XER', ext: '.xer', icon: FileSpreadsheet, supported: true },
    { name: 'Primavera P6 XML', ext: '.xml', icon: FileSpreadsheet, supported: true },
    { name: 'MS Project XML', ext: '.xml', icon: FileSpreadsheet, supported: true },
    { name: 'MS Project MPP', ext: '.mpp', icon: FileSpreadsheet, supported: false },
  ];

  const exportFormats = [
    { name: 'Microsoft Project', ext: '.mpp', icon: FileSpreadsheet },
    { name: 'Primavera P6', ext: '.xer', icon: FileSpreadsheet },
    { name: 'Excel Report', ext: '.xlsx', icon: FileSpreadsheet },
    { name: 'PDF Report', ext: '.pdf', icon: FileText },
    { name: 'CSV Data', ext: '.csv', icon: FileText },
  ];

  // Load data
  useEffect(() => {
    loadProjects();
    loadImportHistory();
  }, []);

  // new_project mode requires the planner grant (CREATE_PROJECT). A non-planner lands on
  // the default 'new_project' mode but cannot perform it (and the option is
  // hidden below), so coerce them to 'new_schedule' to keep the Select coherent.
  useEffect(() => {
    if (!canCreateProject && importMode === 'new_project') {
      setImportMode('new_schedule');
    }
  }, [canCreateProject, importMode]);

  // Load schedules when export project changes
  useEffect(() => {
    if (selectedExportProject) {
      loadSchedules(selectedExportProject);
    } else {
      setSchedules([]);
      setSelectedExportSchedule('');
    }
  }, [selectedExportProject]);

  const loadProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const loadSchedules = async (projectId) => {
    try {
      const response = await api.get(`/schedules?project_id=${projectId}`);
      setSchedules(response.data);
      if (response.data.length > 0) {
        setSelectedExportSchedule(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load schedules:', error);
    }
  };

  const loadImportHistory = async () => {
    try {
      setLoading(true);
      const response = await api.get('/import/history');
      setImportHistory(response.data);
    } catch (error) {
      console.error('Failed to load import history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file extension
      const ext = file.name.toLowerCase().split('.').pop();
      if (ext === 'mpp') {
        toast.error('MPP files are not supported', {
          description: 'Please export to XML format from MS Project (File > Save As > XML format)'
        });
        return;
      }
      if (!['xer', 'xml'].includes(ext)) {
        toast.error('Unsupported file format', {
          description: 'Supported formats: .xer, .xml'
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      const ext = file.name.toLowerCase().split('.').pop();
      if (ext === 'mpp') {
        toast.error('MPP files are not supported', {
          description: 'Please export to XML format from MS Project'
        });
        return;
      }
      if (!['xer', 'xml'].includes(ext)) {
        toast.error('Unsupported file format');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  // Validate file before import (preview)
  const handleValidate = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    try {
      setValidating(true);
      
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post('/import/validate', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setValidationResult(response.data);
      setShowValidationDialog(true);
      
    } catch (error) {
      console.error('Validation failed:', error);
      toast.error('Validation failed', {
        description: apiErrorMessage(error, 'An error occurred during validation')
      });
    } finally {
      setValidating(false);
    }
  };

  // Proceed with import after validation
  const handleProceedImport = () => {
    setShowValidationDialog(false);
    handleUpload();
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    if (importMode === 'new_schedule' && !existingProjectId) {
      toast.error('Please select an existing project');
      return;
    }

    // Per-target guard: don't attempt an import the backend will reject anyway.
    if (!canPerformImport) {
      toast.error('You do not have permission to import into this project');
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('mode', importMode);
      
      if (existingProjectId) {
        formData.append('existing_project_id', existingProjectId);
      }
      if (newProjectName) {
        formData.append('new_project_name', newProjectName);
      }
      if (newScheduleName) {
        formData.append('new_schedule_name', newScheduleName);
      }

      const response = await api.post('/import/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setImportResult(response.data);
      setShowResultDialog(true);
      
      if (response.data.status === 'completed') {
        toast.success('Import successful!', {
          description: `Created ${response.data.summary?.activity_count || 0} activities`
        });
        loadImportHistory();
        loadProjects();
      } else if (response.data.status === 'failed') {
        toast.error('Import failed', {
          description: response.data.error_message || 'Check validation errors'
        });
      }
      
      // Reset form
      setSelectedFile(null);
      setNewProjectName('');
      setNewScheduleName('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Import failed', {
        description: apiErrorMessage(error, 'An error occurred during import')
      });
    } finally {
      setUploading(false);
    }
  };

  // Export functions
  const handleExport = async (format) => {
    if (!selectedExportSchedule) {
      toast.error('Please select a schedule to export');
      return;
    }

    // Export is gated on the SELECTED project, not the active one.
    if (!canImportExportFor(selectedExportProject)) {
      toast.error('You do not have permission to export this project');
      return;
    }

    try {
      setExporting(true);
      
      let endpoint;
      let mediaType;
      let fileExt;
      
      if (format === 'xml') {
        endpoint = `/import/export/${selectedExportSchedule}/xml`;
        mediaType = 'application/xml';
        fileExt = 'xml';
      } else if (format === 'xer') {
        endpoint = `/import/export/${selectedExportSchedule}/xer`;
        mediaType = 'text/plain';
        fileExt = 'xer';
      } else if (format === 'pdf') {
        endpoint = `/import/export/${selectedExportSchedule}/pdf`;
        mediaType = 'application/pdf';
        fileExt = 'pdf';
      } else {
        toast.error('Unsupported export format');
        return;
      }
      
      const response = await api.get(endpoint, {
        responseType: 'blob'
      });
      
      // Get filename from content-disposition header or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `schedule_export.${fileExt}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '');
        }
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export successful!', {
        description: `Downloaded ${filename}`
      });
      
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export PDF', {
        description: apiErrorMessage(error, 'An error occurred during export')
      });
    } finally {
      setExporting(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: { variant: 'default', icon: CheckCircle, className: 'bg-green-100 text-green-700' },
      failed: { variant: 'destructive', icon: AlertCircle, className: 'bg-red-100 text-red-700' },
      pending: { variant: 'secondary', icon: Loader2, className: 'bg-yellow-100 text-yellow-700' },
      importing: { variant: 'secondary', icon: Loader2, className: 'bg-blue-100 text-blue-700' },
    };
    const config = variants[status] || variants.pending;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className={`${config.className} gap-1`}>
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="import-export-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Import / Export
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Transfer schedule data between systems
          </p>
        </div>
      </div>

      <Tabs defaultValue="import" className="w-full">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="import" className="gap-2">
            <Upload className="h-4 w-4" />
            Import
          </TabsTrigger>
          <TabsTrigger value="export" className="gap-2">
            <Download className="h-4 w-4" />
            Export
          </TabsTrigger>
        </TabsList>

        {/* Import Tab */}
        <TabsContent value="import" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Upload Area */}
            <Card className="border-slate-200 lg:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-900">
                  Upload Schedule File
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Drop zone. Enabled for the current mode/target: at file-pick
                    time the default mode is new_project (allowed for any member),
                    so picking a file is unblocked; per-target enforcement for
                    new_schedule lands on the Import button below. */}
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    canPerformImport
                      ? 'border-slate-200 hover:border-slate-400 cursor-pointer'
                      : 'border-slate-100 cursor-not-allowed opacity-60'
                  }`}
                  onDrop={canPerformImport ? handleFileDrop : undefined}
                  onDragOver={canPerformImport ? handleDragOver : undefined}
                  onClick={() => canPerformImport && fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xer,.xml"
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={!canPerformImport}
                  />
                  
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileSpreadsheet className="h-10 w-10 text-blue-500" />
                      <div className="text-left">
                        <p className="text-sm font-medium text-slate-900">{selectedFile.name}</p>
                        <p className="text-xs text-slate-500">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                        <FolderUp className="h-8 w-8 text-slate-400" />
                      </div>
                      <p className="text-sm font-medium text-slate-900 mb-1">
                        Drop your file here
                      </p>
                      <p className="text-xs text-slate-500 mb-4">
                        or click to browse files
                      </p>
                      <p className="text-[10px] text-slate-400">
                        Supported: .xer, .xml (Max 50MB)
                      </p>
                    </>
                  )}
                </div>

                {importMode === 'new_schedule' && existingProjectId && !canImportExportFor(existingProjectId) && (
                  <Badge variant="outline" className="text-[10px] w-full justify-center">
                    Insufficient permissions to import into this project
                  </Badge>
                )}

                {/* Import Options — kept visible so the mode/target can be chosen;
                    the Import button enforces per-target permission. */}
                {selectedFile && (
                  <>
                    <Separator />
                    
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Import Mode</Label>
                        <Select value={importMode} onValueChange={setImportMode}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {/* Create New Project = planner-grant CREATE_PROJECT (planner
                                /super_admin). Hidden for members, who can still add
                                a schedule to a project they hold IMPORT_EXPORT on. */}
                            {canCreateProject && (
                              <SelectItem value="new_project">Create New Project</SelectItem>
                            )}
                            <SelectItem value="new_schedule">Add as New Schedule to Existing Project</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {importMode === 'new_schedule' && (
                        <div className="space-y-2">
                          <Label>Select Project</Label>
                          <Select value={existingProjectId} onValueChange={setExistingProjectId}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a project..." />
                            </SelectTrigger>
                            <SelectContent>
                              {projects.map((project) => (
                                <SelectItem key={project.id} value={project.id}>
                                  {project.name} ({project.code})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {importMode === 'new_project' && (
                        <div className="space-y-2">
                          <Label>Project Name (optional)</Label>
                          <Input
                            value={newProjectName}
                            onChange={(e) => setNewProjectName(e.target.value)}
                            placeholder="Override imported project name..."
                          />
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label>Schedule Name (optional)</Label>
                        <Input
                          value={newScheduleName}
                          onChange={(e) => setNewScheduleName(e.target.value)}
                          placeholder="Override schedule name..."
                        />
                      </div>

                      <div className="flex gap-2">
                        <Button 
                          variant="outline"
                          className="flex-1"
                          onClick={handleValidate}
                          disabled={validating || uploading}
                        >
                          {validating ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Validating...
                            </>
                          ) : (
                            <>
                              <Eye className="h-4 w-4 mr-2" />
                              Preview
                            </>
                          )}
                        </Button>
                        <Button
                          className="flex-1 bg-slate-900 hover:bg-slate-800"
                          onClick={handleUpload}
                          disabled={uploading || validating || !canPerformImport}
                        >
                          {uploading ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Importing...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4 mr-2" />
                              Import
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Supported Formats */}
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-900">
                  Supported Formats
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {importFormats.map((format, i) => (
                  <div 
                    key={i} 
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      format.supported 
                        ? 'bg-slate-50 border-slate-100' 
                        : 'bg-red-50/50 border-red-100'
                    }`}
                  >
                    <format.icon className={`h-5 w-5 ${format.supported ? 'text-slate-400' : 'text-red-300'}`} />
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${format.supported ? 'text-slate-700' : 'text-red-400'}`}>
                        {format.name}
                      </p>
                      <p className="text-xs text-slate-400">{format.ext}</p>
                    </div>
                    {format.supported ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <X className="h-4 w-4 text-red-400" />
                    )}
                  </div>
                ))}
                
                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="flex gap-2">
                    <Info className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-700">
                      <p className="font-medium mb-1">MPP files not supported</p>
                      <p>To import MS Project files, export to XML format first (File → Save As → XML).</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Import History */}
          <Card className="border-slate-200 mt-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-slate-900">
                Import History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : importHistory.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-slate-400">No import history available</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {importHistory.map((item) => (
                    <div 
                      key={item.id} 
                      className="flex items-center gap-4 p-3 rounded-lg bg-slate-50 border border-slate-100"
                    >
                      <FileSpreadsheet className="h-5 w-5 text-slate-400" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {item.file_name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {item.project_name} • {item.activity_count} activities • {formatDate(item.created_at)}
                        </p>
                      </div>
                      {getStatusBadge(item.status)}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Export Tab */}
        <TabsContent value="export" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Export Format Selection */}
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-900">
                  Export Format
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* MS Project XML - Enabled */}
                <Button
                  variant="outline"
                  className="w-full h-auto py-3 px-4 justify-start gap-3 text-left hover:bg-slate-50"
                  onClick={() => handleExport('xml')}
                  disabled={!selectedExportSchedule || exporting || !canImportExportFor(selectedExportProject)}
                >
                  <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-700">Microsoft Project XML</p>
                    <p className="text-xs text-slate-400">.xml</p>
                  </div>
                  {exporting ? (
                    <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 text-blue-500" />
                  )}
                </Button>

                {/* P6 XER - Enabled */}
                <Button
                  variant="outline"
                  className="w-full h-auto py-3 px-4 justify-start gap-3 text-left hover:bg-slate-50"
                  onClick={() => handleExport('xer')}
                  disabled={!selectedExportSchedule || exporting || !canImportExportFor(selectedExportProject)}
                >
                  <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <FileSpreadsheet className="h-5 w-5 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-700">Primavera P6 XER</p>
                    <p className="text-xs text-slate-400">.xer</p>
                  </div>
                  {exporting ? (
                    <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 text-orange-500" />
                  )}
                </Button>

                {/* PDF Report - Enabled */}
                <Button
                  variant="outline"
                  className="w-full h-auto py-3 px-4 justify-start gap-3 text-left hover:bg-slate-50"
                  onClick={() => handleExport('pdf')}
                  disabled={!selectedExportSchedule || exporting || !canImportExportFor(selectedExportProject)}
                  data-testid="export-pdf-button"
                >
                  <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-700">PDF Report</p>
                    <p className="text-xs text-slate-400">.pdf - Schedule with Gantt Chart</p>
                  </div>
                  {exporting ? (
                    <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 text-red-500" />
                  )}
                </Button>

                {/* Coming Soon formats */}
                <div className="pt-2">
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Coming Soon</p>
                  {[
                    { name: 'Excel Report', ext: '.xlsx' },
                    { name: 'CSV Data', ext: '.csv' },
                  ].map((format, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100 opacity-50 mb-2"
                    >
                      <FileText className="h-5 w-5 text-slate-300" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-400">{format.name}</p>
                        <p className="text-xs text-slate-300">{format.ext}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {selectedExportProject && !canImportExportFor(selectedExportProject) && (
                  <Badge variant="outline" className="text-[10px] w-full justify-center">
                    Insufficient permissions for export
                  </Badge>
                )}
              </CardContent>
            </Card>

            {/* Export Settings */}
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-900">
                  Select Schedule
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Project</Label>
                  <Select value={selectedExportProject} onValueChange={setSelectedExportProject}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a project..." />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name} ({project.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedExportProject && (
                  <div className="space-y-2">
                    <Label>Schedule</Label>
                    <Select value={selectedExportSchedule} onValueChange={setSelectedExportSchedule}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a schedule..." />
                      </SelectTrigger>
                      <SelectContent>
                        {schedules.map((schedule) => (
                          <SelectItem key={schedule.id} value={schedule.id}>
                            {schedule.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {!selectedExportProject && (
                  <div className="text-center py-8">
                    <FileSpreadsheet className="h-10 w-10 text-slate-200 mx-auto mb-3" />
                    <p className="text-sm text-slate-400">
                      Select a project to view available schedules
                    </p>
                  </div>
                )}

                {selectedExportProject && schedules.length === 0 && (
                  <div className="text-center py-8">
                    <AlertCircle className="h-10 w-10 text-amber-200 mx-auto mb-3" />
                    <p className="text-sm text-slate-400">
                      No schedules found for this project
                    </p>
                  </div>
                )}

                <Separator />

                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="flex gap-2">
                    <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-blue-700">
                      <p className="font-medium mb-1">Export includes:</p>
                      <ul className="space-y-0.5">
                        <li>• WBS hierarchy</li>
                        <li>• Activities with dates & durations</li>
                        <li>• Relationships (FS, SS, FF, SF)</li>
                        <li>• Progress data</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Import Result Dialog */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {importResult?.status === 'completed' ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Import Successful
                </>
              ) : (
                <>
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  Import Failed
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {importResult?.file_name}
            </DialogDescription>
          </DialogHeader>

          {importResult && (
            <div className="space-y-4">
              {/* Summary */}
              {importResult.summary && (
                <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-slate-900">
                      {importResult.summary.wbs_count}
                    </p>
                    <p className="text-xs text-slate-500">WBS Items</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-slate-900">
                      {importResult.summary.activity_count}
                    </p>
                    <p className="text-xs text-slate-500">Activities</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-slate-900">
                      {importResult.summary.relationship_count}
                    </p>
                    <p className="text-xs text-slate-500">Relationships</p>
                  </div>
                </div>
              )}

              {/* Warnings */}
              {importResult.summary?.warnings?.length > 0 && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <p className="text-xs font-medium text-amber-700 mb-2 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Warnings
                  </p>
                  <ul className="text-xs text-amber-600 space-y-1">
                    {importResult.summary.warnings.map((w, i) => (
                      <li key={i}>• {w}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Validation Errors */}
              {importResult.validation_errors?.length > 0 && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                  <p className="text-xs font-medium text-red-700 mb-2 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Validation Errors
                  </p>
                  <ScrollArea className="max-h-32">
                    <ul className="text-xs text-red-600 space-y-1">
                      {importResult.validation_errors.map((e, i) => (
                        <li key={i}>• {e.field}: {e.message}</li>
                      ))}
                    </ul>
                  </ScrollArea>
                </div>
              )}

              {/* Error message */}
              {importResult.error_message && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                  <p className="text-xs text-red-700">{importResult.error_message}</p>
                </div>
              )}

              {/* Constraints converted to notes */}
              {importResult.summary?.constraints_as_notes?.length > 0 && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-xs font-medium text-blue-700 mb-2 flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    Constraints Converted to Notes
                  </p>
                  <p className="text-xs text-blue-600">
                    {importResult.summary.constraints_as_notes.length} constraint(s) were converted to activity notes
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {importResult?.status === 'completed' ? (
              <div className="flex gap-2 w-full justify-end">
                <Button variant="outline" onClick={() => setShowResultDialog(false)}>
                  Stay Here
                </Button>
                <Button onClick={() => { setShowResultDialog(false); navigate('/projects'); }}>
                  Go to Projects
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            ) : (
              <Button onClick={() => setShowResultDialog(false)}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Validation Preview Dialog */}
      <Dialog open={showValidationDialog} onOpenChange={setShowValidationDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {validationResult?.is_valid ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Validation Passed
                </>
              ) : (
                <>
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  Validation Failed
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {validationResult?.file_name} • {validationResult?.file_type?.replace('_', ' ')}
            </DialogDescription>
          </DialogHeader>

          {validationResult && (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-4 pr-4">
                {/* Summary Stats */}
                <div className="grid grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-xl font-bold text-slate-900">
                      {validationResult.project_name?.substring(0, 20) || 'Unknown'}
                    </p>
                    <p className="text-xs text-slate-500">Project</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-slate-900">
                      {validationResult.wbs_count}
                    </p>
                    <p className="text-xs text-slate-500">WBS Items</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-slate-900">
                      {validationResult.activity_count}
                    </p>
                    <p className="text-xs text-slate-500">Activities</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-slate-900">
                      {validationResult.relationship_count}
                    </p>
                    <p className="text-xs text-slate-500">Relationships</p>
                  </div>
                </div>

                {/* Validation Errors */}
                {validationResult.validation_errors?.length > 0 && (
                  <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                    <p className="text-xs font-medium text-red-700 mb-2 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Validation Errors ({validationResult.validation_errors.length})
                    </p>
                    <ul className="text-xs text-red-600 space-y-1">
                      {validationResult.validation_errors.map((e, i) => (
                        <li key={i}>• {e.field}: {e.message}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Warnings */}
                {validationResult.warnings?.length > 0 && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <p className="text-xs font-medium text-amber-700 mb-2 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Warnings ({validationResult.warnings.length})
                    </p>
                    <ul className="text-xs text-amber-600 space-y-1">
                      {validationResult.warnings.map((w, i) => (
                        <li key={i}>• {w}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Activities Preview */}
                {validationResult.activities_preview?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                      Activities Preview (first 10)
                    </p>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="text-xs py-2">Code</TableHead>
                            <TableHead className="text-xs py-2">Name</TableHead>
                            <TableHead className="text-xs py-2">Duration</TableHead>
                            <TableHead className="text-xs py-2">Start</TableHead>
                            <TableHead className="text-xs py-2">Finish</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {validationResult.activities_preview.map((act, i) => (
                            <TableRow key={i}>
                              <TableCell className="text-xs py-2 font-mono">{act.code}</TableCell>
                              <TableCell className="text-xs py-2 max-w-[200px] truncate">{act.name}</TableCell>
                              <TableCell className="text-xs py-2">{act.duration}d</TableCell>
                              <TableCell className="text-xs py-2">{act.planned_start || '-'}</TableCell>
                              <TableCell className="text-xs py-2">{act.planned_finish || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {validationResult.activity_count > 10 && (
                      <p className="text-xs text-slate-400 mt-1 text-center">
                        + {validationResult.activity_count - 10} more activities
                      </p>
                    )}
                  </div>
                )}

                {/* Relationships Preview */}
                {validationResult.relationships_preview?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                      Relationships Preview (first 10)
                    </p>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="text-xs py-2">Predecessor</TableHead>
                            <TableHead className="text-xs py-2">Type</TableHead>
                            <TableHead className="text-xs py-2">Successor</TableHead>
                            <TableHead className="text-xs py-2">Lag</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {validationResult.relationships_preview.map((rel, i) => (
                            <TableRow key={i}>
                              <TableCell className="text-xs py-2 font-mono">{rel.predecessor}</TableCell>
                              <TableCell className="text-xs py-2">
                                <Badge variant="outline" className="text-[10px]">{rel.type}</Badge>
                              </TableCell>
                              <TableCell className="text-xs py-2 font-mono">{rel.successor}</TableCell>
                              <TableCell className="text-xs py-2">{rel.lag || 0}d</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {validationResult.relationship_count > 10 && (
                      <p className="text-xs text-slate-400 mt-1 text-center">
                        + {validationResult.relationship_count - 10} more relationships
                      </p>
                    )}
                  </div>
                )}

                {/* Constraints as Notes */}
                {validationResult.constraints_as_notes?.length > 0 && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-xs font-medium text-blue-700 mb-2 flex items-center gap-1">
                      <Info className="h-3 w-3" />
                      {validationResult.constraints_as_notes.length} constraint(s) will be converted to notes
                    </p>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowValidationDialog(false)}>
              Cancel
            </Button>
            {validationResult?.is_valid && (
              <Button 
                className="bg-slate-900 hover:bg-slate-800"
                onClick={handleProceedImport}
              >
                <ArrowRight className="h-4 w-4 mr-2" />
                Proceed with Import
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImportExportPage;
